import { Building, PanelLeft, Plus, Search, SlidersHorizontal } from 'lucide-react'
import { useEquipmentScope } from './equipmentScope'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  EQUIPMENT_PURPOSE_LABELS,
  type EquipmentPurpose,
} from '@/types/equipment'

export const ALL_GROUPS = 'all' as const
export type GroupFilter = string

/**
 * Purpose filter, independent of the group filter.
 *
 * The two narrow together rather than replacing each other: group names a
 * manufacturing stage and purpose says whether the equipment acts on material
 * or holds it, so "Cryopreservation + Storage" is a question worth asking.
 */
export const ALL_PURPOSES = 'all' as const
export type PurposeFilter = EquipmentPurpose | typeof ALL_PURPOSES

/**
 * Maintained filter — a third independent axis, same rules as the two above.
 *
 * Everything stays in ONE table: a non-maintained type is ordinary equipment
 * that simply has no service cycle, so splitting it onto its own tab would hide
 * half the fleet from anyone not looking for it. This narrows the same list
 * instead.
 *
 * String values rather than booleans because Radix's Select is string-keyed;
 * `maintainedFromFilter` converts back at the edge.
 */
export const ALL_MAINTAINED = 'all' as const
export type MaintainedFilter = typeof ALL_MAINTAINED | 'maintained' | 'unmaintained'

/** Filter value → the tri-state `filterEquipmentTypes` expects. */
export function maintainedFromFilter(value: MaintainedFilter): boolean | null {
  if (value === ALL_MAINTAINED) return null
  return value === 'maintained'
}

export function EquipmentToolbar({
  search,
  onSearchChange,
  group,
  onGroupChange,
  groups,
  purpose,
  onPurposeChange,
  maintained,
  onMaintainedChange,
  onManageGroups,
  sort,
  onSortChange,
  onAdd,
  addLabel,
  onLayout,
  onBuilding,
}: {
  search: string
  onSearchChange: (value: string) => void
  group: GroupFilter
  onGroupChange: (value: GroupFilter) => void
  /** Group names to offer in the filter (fetched from the API). */
  groups: string[]
  purpose: PurposeFilter
  onPurposeChange: (value: PurposeFilter) => void
  /** Omitted hides the control; both the unit and type views pass it. */
  maintained?: MaintainedFilter
  onMaintainedChange?: (value: MaintainedFilter) => void
  onManageGroups: () => void
  /** Current sort, e.g. "equipmentId:asc". */
  sort: string
  onSortChange: (value: string) => void
  onAdd: () => void
  addLabel: string
  /** Cleaning only (Cleaning Visual, z8nrz7f731): the campus layout and its set-up. */
  onLayout?: () => void
  onBuilding?: () => void
}) {
  const scope = useEquipmentScope()
  const { copy } = scope
  return (
    <div className='flex flex-wrap items-center gap-2.5'>
      <div className='relative min-w-[240px] flex-1 max-w-sm'>
        <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={copy.search}
          className='pl-9'
        />
      </div>

      <Select value={group} onValueChange={(v) => onGroupChange(v as GroupFilter)}>
        <SelectTrigger className='w-[240px] shrink-0'>
          <SelectValue placeholder='All Groups' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_GROUPS}>All Groups</SelectItem>
          {groups.map((g) => (
            <SelectItem key={g} value={g}>
              {g}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {scope.choosePurpose ? (
        <Select value={purpose} onValueChange={(v) => onPurposeChange(v as PurposeFilter)}>
          <SelectTrigger className='w-[170px] shrink-0'>
            <SelectValue placeholder='All purposes' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_PURPOSES}>All purposes</SelectItem>
            {scope.purposes.map((p) => (
              <SelectItem key={p} value={p}>
                {EQUIPMENT_PURPOSE_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        // The Cleaning page's forced filter (spec: "Forced Filter in CLN Icon to
        // ROOM"): shown, so the page says what it is listing, but locked — the
        // page lists rooms and nothing else (z8nrz7ewt3).
        <Select value={scope.purposes[0]} disabled>
          <SelectTrigger
            className='w-[170px] shrink-0 disabled:cursor-default disabled:opacity-100'
            aria-label='Purpose'
            title={`This page lists ${copy.unitsLower} only`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scope.purposes.map((p) => (
              <SelectItem key={p} value={p}>
                {EQUIPMENT_PURPOSE_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {maintained !== undefined && onMaintainedChange && (
        <Select
          value={maintained}
          onValueChange={(v) => onMaintainedChange(v as MaintainedFilter)}
        >
          <SelectTrigger className='w-[190px] shrink-0'>
            <SelectValue placeholder={copy.maintainedAll} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_MAINTAINED}>{copy.maintainedAll}</SelectItem>
            <SelectItem value='maintained'>{copy.maintained}</SelectItem>
            <SelectItem value='unmaintained'>{copy.unmaintained}</SelectItem>
          </SelectContent>
        </Select>
      )}

      <Button
        variant='outline'
        size='default'
        className='shrink-0 gap-1.5'
        onClick={onManageGroups}
      >
        <SlidersHorizontal className='h-4 w-4' />
        Manage Groups
      </Button>

      <Select value={sort} onValueChange={onSortChange}>
        <SelectTrigger className='w-[168px] shrink-0'>
          <SelectValue placeholder='Sort' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='equipmentId:asc'>{copy.unitId} (A–Z)</SelectItem>
          <SelectItem value='equipmentId:desc'>{copy.unitId} (Z–A)</SelectItem>
          <SelectItem value='equipmentType:asc'>Type (A–Z)</SelectItem>
          <SelectItem value='equipmentType:desc'>Type (Z–A)</SelectItem>
          <SelectItem value='group:asc'>Group (A–Z)</SelectItem>
          <SelectItem value='group:desc'>Group (Z–A)</SelectItem>
          <SelectItem value='location:asc'>Location (A–Z)</SelectItem>
          <SelectItem value='location:desc'>Location (Z–A)</SelectItem>
        </SelectContent>
      </Select>

      <div className='flex-1' />

      {/* Right-aligned as one unit with Add, so a narrow window wraps them together. */}
      <div className='ml-auto flex shrink-0 items-center gap-2.5'>
        {onLayout && (
          <Button variant='outline' size='default' className='gap-1.5' onClick={onLayout}>
            <PanelLeft className='h-4 w-4' />
            Layout
          </Button>
        )}
        {onBuilding && (
          <Button variant='outline' size='default' className='gap-1.5' onClick={onBuilding}>
            <Building className='h-4 w-4' />
            Buildings
          </Button>
        )}
        <Button size='default' className='gap-1.5' onClick={onAdd}>
          <Plus className='h-4 w-4' />
          {addLabel}
        </Button>
      </div>
    </div>
  )
}
